import { useState, useEffect } from "react";
import { RADIUS } from "../../theme/tokens";
import { useThemeContext } from "../../theme/ThemeContext";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { calcSection, parseShift } from "../../utils/timeUtils";
import { lookupRouteCache } from "../../utils/routeCache";
import { appendRemark, REMARK_PATTERN } from "../../utils/entryUtils";
import { AirlineSelector, SectionDivider, FieldLabel, TimeInput, inputStyle, Button } from "../shared";
import { overlayBackdrop } from "../../theme/styles";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

const STATUS_OPTIONS = ["SUCCESS", "PARTIAL SUCCESS", "UNSUCCESSFUL", "REBOOKED", "PROTECTED", "UNABLE TO CONFIRM"];


const emptyForm = () => ({
  airline: "", inFlt: "", origin: "", inGate: "", sta: "", ata: "", eta: "",
  remark: "", section: "active",
});
const emptyOutbound = () => ({
  outAirline: "", outFlt: "", dest: "", outGate: "", std: "", etd: "", atd: "",
  ttlPax: "", paxExp: "", paxRbk: "", ttlBags: "", bagsExp: "",
  status: "", cancelled: false,
});

function AddEntryModal({ onClose, onSave, shift, initialEntry, onDelete, onReopen, activeSCCO, airline, SA, GLASS, STATUS_STYLES }) {
  const { TYPOGRAPHY, tokens } = useThemeContext();
  const isDark = SA.black === "#060912";
  useLockBodyScroll();
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 768;
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });
  const [step, setStep] = useState(1);
  const [confirmDel, setConfirmDel] = useState(false);
  const [form, setForm] = useState(() => {
    if (!initialEntry) return emptyForm();
    return {
      ...emptyForm(),
      ...initialEntry,
      airline: initialEntry.inAirline || (initialEntry.inFlt || "").match(/^[A-Z]{2}/)?.[0] || "",
      inFlt:   (initialEntry.inFlt || "").replace(/^[A-Z]{2}/, ""),
      inGate:  initialEntry.inGate || "",
    };
  });
  const [outbounds, setOutbounds] = useState(() => {
    if (!initialEntry) return [emptyOutbound()];
    return [{
      ...emptyOutbound(),
      outAirline: initialEntry.outAirline || (initialEntry.outFlt || "").match(/^[A-Z]{2}/)?.[0] || "",
      outFlt:     (initialEntry.outFlt || "").replace(/^[A-Z]{2}/, ""),
      outGate:    initialEntry.outGate || "",
      dest: initialEntry.dest || "", std: initialEntry.std || "",
      etd: initialEntry.etd || "", atd: initialEntry.atd || "",
      ttlPax: initialEntry.ttlPax || "", paxExp: initialEntry.paxExp || "",
      paxRbk: initialEntry.paxRbk || "", ttlBags: initialEntry.ttlBags || "",
      bagsExp: initialEntry.bagsExp || "", status: initialEntry.status || "",
      cancelled: initialEntry.cancelled || false,
    }];
  });
  // Track whether the user has manually edited each gate field this session.
  // Persists the existing DB flag when editing; set to true on any keystroke,
  // false when the field is cleared — so the Supabase trigger knows to protect it.
  const [inGateManual,  setInGateManual]  = useState(initialEntry?.inGateManual  || false);
  const [outGateManual, setOutGateManual] = useState(initialEntry?.outGateManual || false);
  const [newRemark,         setNewRemark]         = useState("");
  const [editingRemarkIdx,  setEditingRemarkIdx]  = useState(null);
  const [editingRemarkText, setEditingRemarkText] = useState("");

  const sccoName = activeSCCO?.name || null;
  const addRemark = () => {
    if (!newRemark.trim()) return;
    setForm(f => ({ ...f, remark: appendRemark(f.remark, newRemark.toUpperCase(), airline, sccoName) }));
    setNewRemark("");
  };

  const updateOutbound = (idx, key, val) =>
    setOutbounds(prev => prev.map((o, i) => i === idx ? { ...o, [key]: val } : o));

  const addOutbound = () => {
    setOutbounds(prev => [...prev, emptyOutbound()]);
  };

  const removeOutbound = (idx) => {
    setOutbounds(prev => prev.filter((_, i) => i !== idx));
  };

  // Auto-lookup from cache as user types
  useEffect(() => {
    if (form.airline.length === 2 && form.inFlt.length >= 1) {
      const code   = (form.airline + form.inFlt).toUpperCase();
      const cached = lookupRouteCache(code, "in");
      if (cached) {
        setForm(f => ({ ...f, origin: cached.origin || f.origin }));
      }
    }
  }, [form.airline, form.inFlt]);

  // Derived key — avoids listing `outbounds` directly, which would cause an update loop
  // since the effect calls updateOutbound (which mutates outbounds)
  const outboundRouteKey = outbounds.map(o => (o.outAirline||"") + (o.outFlt||"")).join(",");
  useEffect(() => {
    outbounds.forEach((ob, idx) => {
      if (ob && ob.outAirline && ob.outAirline.length === 2 && ob.outFlt && ob.outFlt.length >= 1) {
        const code = (ob.outAirline + ob.outFlt).toUpperCase();
        const cached = lookupRouteCache(code, "out");
        if (cached && !ob.dest) {
          updateOutbound(idx, "dest", cached.dest);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboundRouteKey]);

  const UPPERCASE_FIELDS = new Set(["inFlt", "origin", "dest", "airline", "inAirline", "outAirline", "outFlt"]);
  const handleChange = (e) => { const { name, value } = e.target; setForm(f => ({ ...f, [name]: UPPERCASE_FIELDS.has(name) ? value.toUpperCase() : value })); };

  const handleSubmit = () => {
    const inPrefix = form.airline.toUpperCase();
    const rawInFlt = form.inFlt.toUpperCase();
    const inFlt = inPrefix && !rawInFlt.startsWith(inPrefix) ? inPrefix + rawInFlt : rawInFlt;
    if (!inFlt) return;
    const validOuts = outbounds.filter(o => o.outFlt.trim());
    if (!validOuts.length) return;
    // Certain statuses require a remark
    const REMARK_REQUIRED = new Set(["UNABLE TO CONFIRM", "PROTECTED", "PARTIAL SUCCESS", "REBOOKED", "UNSUCCESSFUL"]);
    const remarkRequiredStatus = validOuts.find(o => REMARK_REQUIRED.has(o.status));
    if (remarkRequiredStatus && !form.remark.trim() && !newRemark.trim()) {
      alert(`A remark is required when status is "${remarkRequiredStatus.status}".`);
      return;
    }
    // Commit any pending remark text before saving
    const finalRemark = newRemark.trim()
      ? appendRemark(form.remark, newRemark.toUpperCase(), airline, sccoName)
      : form.remark;
    validOuts.forEach((ob, i) => {
      const outPrefix = ob.outAirline.toUpperCase();
      const rawOutFlt = ob.outFlt.toUpperCase();
      const outFlt = outPrefix && !rawOutFlt.startsWith(outPrefix) ? outPrefix + rawOutFlt : rawOutFlt;
      const remark = finalRemark;
      onSave({
        ...form, ...ob, inFlt, outFlt, remark,
        inGateManual, outGateManual,
        id: (initialEntry && i === 0) ? initialEntry.id : Date.now() + i,
      });
    });
    onClose();
  };

  // Wizard navigation state
  const step2Unlocked = !!initialEntry || (form.airline.length >= 2 && !!form.inFlt.trim() && !!form.origin.trim());
  const step3Unlocked = !!initialEntry || outbounds.some(o => o.outFlt.trim());

  // Step indicator
  const BLUE_ACTIVE_BG     = isDark ? "rgba(91,142,255,0.18)" : "rgba(37,99,235,0.10)";
  const BLUE_ACTIVE_BORDER = isDark ? "rgba(91,142,255,0.8)"  : "#2563EB";
  const BLUE_ACTIVE_COLOR  = isDark ? "#8ab4ff"               : "#2563EB";
  const BLUE_DONE_BG       = isDark ? "rgba(91,142,255,0.12)" : "rgba(37,99,235,0.08)";
  const BLUE_DONE_BORDER   = isDark ? "rgba(91,142,255,0.45)" : "rgba(37,99,235,0.4)";

  const renderStepIndicator = () => {
    const steps = [{ n: 1, label: "Inbound" }, { n: 2, label: "Outbound" }, { n: 3, label: "Details" }];
    const items = [];
    steps.forEach((s, i) => {
      const active  = step === s.n;
      const locked  = !initialEntry && (s.n === 2 ? !step2Unlocked : s.n === 3 ? !step3Unlocked : false);
      const done    = !active && !locked;
      const showCheck = done && (step > s.n || !!initialEntry);
      const clickable = !active && !locked;

      if (i > 0) {
        items.push(
          <div key={`line-${i}`} style={{
            flex: 1, height: 1, marginTop: 14, alignSelf: "flex-start",
            background: step > i ? BLUE_DONE_BORDER : SA.borderGray,
          }} />
        );
      }
      items.push(
        <div key={s.n} onClick={clickable ? () => setStep(s.n) : undefined}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: clickable ? "pointer" : "default" }}>
          <div style={{
            width: 28, height: 28, borderRadius: RADIUS.circle,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: active ? BLUE_ACTIVE_BG : locked ? "transparent" : BLUE_DONE_BG,
            border: `1px solid ${active ? BLUE_ACTIVE_BORDER : locked ? SA.borderGray : BLUE_DONE_BORDER}`,
          }}>
            {showCheck
              ? <span style={{ color: BLUE_ACTIVE_COLOR, fontSize: 12, lineHeight: 1 }}>✓</span>
              : <span style={{ fontSize: TYPOGRAPHY.tiny, fontFamily: "'Helvetica Neue',Arial,sans-serif",
                  color: active ? BLUE_ACTIVE_COLOR : locked ? SA.borderGray : BLUE_ACTIVE_COLOR,
                  fontWeight: active ? 700 : 400 }}>{s.n}</span>
            }
          </div>
          <span style={{ fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.12em", textTransform: "uppercase",
            fontFamily: "'Helvetica Neue',Arial,sans-serif",
            color: active ? SA.white : locked ? SA.borderGray : SA.silver }}>
            {s.label}
          </span>
        </div>
      );
    });
    return (
      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 20 }}>
        {items}
      </div>
    );
  };

  // Step 1 — Inbound flight
  const renderStep1 = () => (
    <>
      <SectionDivider SA={SA}>Inbound Flight</SectionDivider>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-end" }}>
        <AirlineSelector value={form.airline} onChange={val => setForm(f => ({ ...f, airline: val }))} SA={SA} />
        <div style={{ display: "flex", flexDirection: "column", flex: "0 0 72px" }}>
          <FieldLabel SA={SA}>Flight No.</FieldLabel>
          <input name="inFlt" value={form.inFlt} onChange={handleChange} placeholder="777" inputMode="numeric"
            style={{ ...inputStyle(false, SA), textTransform: "uppercase", padding: "10px 8px" }} />
        </div>
        <div style={{ flex: "0 0 66px" }}>
          <FieldLabel SA={SA}>Origin</FieldLabel>
          <input name="origin" value={form.origin} onChange={handleChange} placeholder="TPE"
            style={{ ...inputStyle(false, SA), textTransform: "uppercase", padding: "10px 8px" }} />
        </div>
        <div style={{ flex: "0 0 66px" }}>
          <FieldLabel SA={SA}>Gate</FieldLabel>
          <input name="inGate" value={form.inGate || ""}
            onChange={e => { handleChange(e); setInGateManual(e.target.value.trim() !== ""); }}
            placeholder="B18" style={{ ...inputStyle(false, SA), textTransform: "uppercase", padding: "10px 8px" }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
        <TimeInput label="Scheduled" name="sta" value={form.sta} onChange={handleChange} SA={SA} />
        <TimeInput label="Estimated" name="eta" value={form.eta} onChange={handleChange} SA={SA} />
        <TimeInput label="Actual" name="ata" value={form.ata} onChange={handleChange} SA={SA} />
      </div>
    </>
  );

  // Step 2 — Outbound flight(s)
  const renderStep2 = () => (
    <>
      {outbounds.map((ob, idx) => (
        <div key={idx} style={{ marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <SectionDivider SA={SA}>{outbounds.length > 1 ? `Outbound Flight ${idx + 1}` : "Outbound Flight"}</SectionDivider>
            </div>
            {idx > 0 && (
              <Button variant="ghost" onClick={() => removeOutbound(idx)}
                style={{ fontSize: TYPOGRAPHY.caption, letterSpacing: "0.1em",
                  color: "#c06060", marginBottom: 14, flexShrink: 0, marginLeft: 8 }}>
                Remove
              </Button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-end" }}>
            <AirlineSelector value={ob.outAirline} onChange={val => updateOutbound(idx, "outAirline", val)} SA={SA} />
            <div style={{ display: "flex", flexDirection: "column", flex: "0 0 72px" }}>
              <FieldLabel SA={SA}>Flight No.</FieldLabel>
              <input value={ob.outFlt} onChange={e => updateOutbound(idx, "outFlt", e.target.value.toUpperCase())}
                placeholder="1158" inputMode="numeric" autoComplete="off" style={{ ...inputStyle(false, SA), textTransform: "uppercase", padding: "10px 8px" }} />
            </div>
            <div style={{ flex: "0 0 66px" }}>
              <FieldLabel SA={SA}>Dest</FieldLabel>
              <input value={ob.dest} onChange={e => updateOutbound(idx, "dest", e.target.value.toUpperCase())}
                placeholder="HNL" style={{ ...inputStyle(false, SA), textTransform: "uppercase", padding: "10px 8px" }} />
            </div>
            <div style={{ flex: "0 0 66px" }}>
              <FieldLabel SA={SA}>Gate</FieldLabel>
              <input value={ob.outGate || ""}
                onChange={e => { updateOutbound(idx, "outGate", e.target.value.toUpperCase()); setOutGateManual(e.target.value.trim() !== ""); }}
                placeholder="B20" style={{ ...inputStyle(false, SA), textTransform: "uppercase", padding: "10px 8px" }} />
            </div>
            <Button onClick={() => updateOutbound(idx, "cancelled", !ob.cancelled)}
              style={{ marginLeft: "auto", padding: "11px 6px", borderRadius: 20, flexShrink: 0,
                fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.16em",
                textTransform: "uppercase", fontWeight: ob.cancelled ? 700 : 400,
                background: ob.cancelled ? (isDark ? "#3a1a1a" : "#fef2f2") : "transparent",
                border: `1px solid ${ob.cancelled ? (isDark ? "#7a2a2a" : "rgba(198,40,40,0.35)") : SA.borderGray}`,
                color: ob.cancelled ? (isDark ? "#c06060" : "#C62828") : SA.silver, gap: 6 }}>
              <span style={{ width: 5, height: 5, borderRadius: RADIUS.circle, background: ob.cancelled ? (isDark ? "#c06060" : "#EF5350") : SA.borderGray, display: "inline-block" }} />
              {ob.cancelled ? "Flight Cancelled" : "Mark as Cancelled"}
            </Button>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <FieldLabel SA={SA}>Scheduled</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" placeholder="HH:MM" value={ob.std}
                onChange={e => { let r = e.target.value.replace(/[^0-9]/g,"").slice(0,4); updateOutbound(idx,"std", r.length>=3?r.slice(0,2)+":"+r.slice(2):r); }}
                style={inputStyle(false, SA)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <FieldLabel SA={SA}>Estimated</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" placeholder="HH:MM" value={ob.etd}
                disabled={ob.cancelled}
                onChange={e => { let r = e.target.value.replace(/[^0-9]/g,"").slice(0,4); updateOutbound(idx,"etd", r.length>=3?r.slice(0,2)+":"+r.slice(2):r); }}
                style={{ ...inputStyle(false, SA), opacity: ob.cancelled ? 0.3 : 1 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <FieldLabel SA={SA}>Actual</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" placeholder="HH:MM" value={ob.atd}
                disabled={ob.cancelled}
                onChange={e => { let r = e.target.value.replace(/[^0-9]/g,"").slice(0,4); updateOutbound(idx,"atd", r.length>=3?r.slice(0,2)+":"+r.slice(2):r); }}
                style={{ ...inputStyle(false, SA), opacity: ob.cancelled ? 0.3 : 1 }} />
            </div>
          </div>
          {!initialEntry && idx === outbounds.length - 1 && (
            <Button fullWidth onClick={addOutbound}
              style={{ marginBottom: 10, padding: "11px", borderRadius: 2,
                background: "transparent", border: `1px dashed ${SA.borderGray}`,
                color: SA.silver, fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.18em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", gap: 8 }}>
              + Add Another Outbound Flight
            </Button>
          )}
        </div>
      ))}
    </>
  );

  // Step 3 — PAX/Bags + Status + Remarks
  const renderPaxBagsCards = (ob, idx) => {
    const paxTtl = Number(ob.ttlPax)||0, paxAcc = (Number(ob.paxExp)||0)+(Number(ob.paxRbk)||0), paxUnk = paxTtl>0?paxTtl-paxAcc:0;
    const bagsTtl = Number(ob.ttlBags)||0, bagsAcc = Number(ob.bagsExp)||0, bagsMissing = bagsTtl>0?bagsTtl-bagsAcc:0;
    const paxBorder  = paxTtl>0  ? (paxUnk>0    ? (isDark?"#7a2a2a":"rgba(198,40,40,0.35)") : (isDark?"#4a7a4a":"rgba(76,175,80,0.40)"))  : SA.borderGray;
    const bagsBorder = bagsTtl>0 ? (bagsMissing>0? (isDark?"#7a2a2a":"rgba(198,40,40,0.35)") : (isDark?"#4a7a4a":"rgba(76,175,80,0.40)")) : SA.borderGray;
    const cardBase = (border) => ({ background: SA.darkGray, border: `1px solid ${border}`, borderRadius: RADIUS.md, padding: 12 });

    return (
      <div style={{ display: "grid", gridTemplateColumns: isWide ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 12 }}>
        {/* PAX card */}
        <div style={cardBase(paxBorder)}>
          <FieldLabel SA={SA}>Passengers</FieldLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8, marginBottom: paxTtl>0 ? 8 : 0 }}>
            <div>
              <FieldLabel SA={SA}>Total</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" value={ob.ttlPax}
                onChange={e => updateOutbound(idx,"ttlPax",e.target.value)} style={inputStyle(false, SA)} />
            </div>
            <div style={{ opacity: ob.cancelled ? 0.3 : 1, pointerEvents: ob.cancelled ? "none" : "auto" }}>
              <FieldLabel SA={SA}>Expedited</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" value={ob.paxExp}
                onChange={e => updateOutbound(idx,"paxExp",e.target.value)} style={inputStyle(false, SA)} />
            </div>
            <div>
              <FieldLabel SA={SA}>Rebooked</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" value={ob.paxRbk}
                onChange={e => updateOutbound(idx,"paxRbk",e.target.value)} style={inputStyle(false, SA)} />
            </div>
          </div>
          {paxTtl>0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: paxUnk>0?(isDark?"#c06060":"#C62828"):(isDark?"#8fbf8f":"#2E7D32"), fontFamily: "'Helvetica Neue',Arial,sans-serif", letterSpacing: "0.1em" }}>
                  {paxUnk>0?`${paxUnk} UNACCOUNTED`:"ALL ACCOUNTED"}
                </span>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>{paxAcc}/{paxTtl}</span>
              </div>
              <div style={{ height: 2, background: SA.borderGray, borderRadius: 1 }}>
                <div style={{ height: 2, width: `${Math.min(paxTtl>0?paxAcc/paxTtl*100:0,100)}%`, background: paxUnk>0?(isDark?"#7a2a2a":"#EF5350"):(isDark?"#4a7a4a":"#4CAF50"), borderRadius: 1 }} />
              </div>
            </>
          )}
        </div>

        {/* Bags card */}
        <div style={cardBase(bagsBorder)}>
          <FieldLabel SA={SA}>Baggage</FieldLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, marginBottom: bagsTtl>0 ? 8 : 0 }}>
            <div>
              <FieldLabel SA={SA}>Total</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" value={ob.ttlBags}
                onChange={e => updateOutbound(idx,"ttlBags",e.target.value)} style={inputStyle(false, SA)} />
            </div>
            <div style={{ opacity: ob.cancelled ? 0.3 : 1, pointerEvents: ob.cancelled ? "none" : "auto" }}>
              <FieldLabel SA={SA}>Expedited</FieldLabel>
              <input type="text" inputMode="numeric" autoComplete="off" value={ob.bagsExp}
                onChange={e => updateOutbound(idx,"bagsExp",e.target.value)} style={inputStyle(false, SA)} />
            </div>
          </div>
          {bagsTtl>0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: bagsMissing>0?(isDark?"#c06060":"#C62828"):(isDark?"#8fbf8f":"#2E7D32"), fontFamily: "'Helvetica Neue',Arial,sans-serif", letterSpacing: "0.1em" }}>
                  {bagsMissing>0?`${bagsMissing} NOT EXPEDITED`:"ALL ACCOUNTED"}
                </span>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>{bagsAcc}/{bagsTtl}</span>
              </div>
              <div style={{ height: 2, background: SA.borderGray, borderRadius: 1 }}>
                <div style={{ height: 2, width: `${Math.min(bagsTtl>0?bagsAcc/bagsTtl*100:0,100)}%`, background: bagsMissing>0?(isDark?"#7a2a2a":"#EF5350"):(isDark?"#4a7a4a":"#4CAF50"), borderRadius: 1 }} />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderStatusButtons = (ob, idx) => {
    const isOutlook = !initialEntry && calcSection({...form,...ob}, parseShift(shift)) === "outlook";
    const statusOpts = isOutlook ? ["REBOOKED", "PROTECTED"] : STATUS_OPTIONS;
    return (
      <>
        <SectionDivider SA={SA}>Status</SectionDivider>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {statusOpts.map(s => {
            const st = STATUS_STYLES[s]; const sel = ob.status === s;
            return (
              <Button key={s} onClick={() => updateOutbound(idx, "status", sel ? "" : s)}
                style={{ padding: "13px 10px", borderRadius: RADIUS.sm,
                  border: `1px solid ${sel ? st.border : SA.borderGray}`,
                  background: sel ? st.bg : "transparent", color: sel ? st.text : SA.silver,
                  fontSize: TYPOGRAPHY.caption, fontWeight: sel ? 700 : 400, letterSpacing: "0.10em",
                  fontFamily: "'Helvetica Neue',Arial,sans-serif", textTransform: "uppercase",
                  flexDirection: "column", alignItems: "center", gap: 4,
                  lineHeight: 1.2, textAlign: "center" }}>
                <span style={{ width: 6, height: 6, borderRadius: RADIUS.circle, background: sel ? st.dot : SA.borderGray, display: "block" }} />
                {s}
              </Button>
            );
          })}
        </div>
      </>
    );
  };

  const renderStep3 = () => (
    <>
      {outbounds.map((ob, idx) => (
        <div key={idx}>
          {outbounds.length > 1 && (
            <SectionDivider SA={SA}>{`Outbound Flight ${idx + 1}`}</SectionDivider>
          )}
          {renderPaxBagsCards(ob, idx)}
          {renderStatusButtons(ob, idx)}
        </div>
      ))}

      <SectionDivider SA={SA}>Remarks</SectionDivider>
      {form.remark.trim() ? (
        <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {form.remark.split("\n").filter(Boolean).map((line, i, arr) => {
            const match = line.match(REMARK_PATTERN);
            const canDelete = !!activeSCCO;
            const canEdit = !!(activeSCCO && match && match[1] === `UA/${activeSCCO.name}`);
            const isEditing = editingRemarkIdx === i;

            const deleteRemark = () => {
              const updated = arr.filter((_, j) => j !== i).join("\n");
              setForm(f => ({ ...f, remark: updated }));
              if (editingRemarkIdx === i) { setEditingRemarkIdx(null); setEditingRemarkText(""); }
            };

            const saveEdit = () => {
              if (!editingRemarkText.trim()) return;
              const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
              const newLine = `[${match[1]} ${time}] ${editingRemarkText.trim().toUpperCase()}`;
              const updated = arr.map((l, j) => j === i ? newLine : l).join("\n");
              setForm(f => ({ ...f, remark: updated }));
              setEditingRemarkIdx(null);
              setEditingRemarkText("");
            };

            const cancelEdit = () => { setEditingRemarkIdx(null); setEditingRemarkText(""); };

            return (
              <div key={i} style={{ background: SA.darkGray, border: `1px solid ${isEditing ? "rgba(255,255,255,0.28)" : SA.borderGray}`,
                borderRadius: RADIUS.md, padding: "8px 12px",
                display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  {match && (
                    <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", marginBottom: 3, letterSpacing: "0.06em" }}>
                      {match[1]} · {match[2]}
                    </div>
                  )}
                  {isEditing ? (
                    <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                      <input
                        autoFocus
                        value={editingRemarkText}
                        onChange={e => setEditingRemarkText(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                        style={{ ...inputStyle(false, SA), flex: 1, textTransform: "uppercase", letterSpacing: "0.04em", padding: "6px 10px" }}
                      />
                      <button onClick={saveEdit}
                        style={{ background: "rgba(91,142,255,0.18)", border: "1px solid rgba(91,142,255,0.4)",
                          borderRadius: RADIUS.sm, color: "#8ab0ff", fontSize: TYPOGRAPHY.tiny,
                          letterSpacing: "0.1em", padding: "4px 10px", cursor: "pointer", flexShrink: 0 }}>
                        SAVE
                      </button>
                      <button onClick={cancelEdit}
                        style={{ background: "none", border: "none", color: SA.borderGray,
                          fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.1em", padding: "4px 6px", cursor: "pointer", flexShrink: 0 }}>
                        CANCEL
                      </button>
                    </div>
                  ) : (
                    <div style={{ fontSize: TYPOGRAPHY.body, color: SA.lightSilver, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                      {match ? match[3] : line}
                    </div>
                  )}
                </div>
                {!isEditing && canEdit && (
                  <button onClick={() => { setEditingRemarkIdx(i); setEditingRemarkText(match[3]); }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
                      color: SA.silver, fontSize: 13, lineHeight: 1, flexShrink: 0, transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = SA.lightSilver}
                    onMouseLeave={e => e.currentTarget.style.color = SA.silver}
                    title="Edit remark">
                    ✎
                  </button>
                )}
                {!isEditing && canDelete && (
                  <button onClick={deleteRemark}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
                      color: SA.silver, fontSize: 14, lineHeight: 1, flexShrink: 0, transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#c06060"}
                    onMouseLeave={e => e.currentTarget.style.color = SA.silver}>
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input
          value={newRemark}
          onChange={e => setNewRemark(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addRemark()}
          placeholder="ADD REMARK…"
          style={{ ...inputStyle(false, SA), flex: 1, textTransform: "uppercase", letterSpacing: "0.04em" }}
        />
        <Button onClick={addRemark} disabled={!newRemark.trim()}
          style={{ padding: "0 16px", borderRadius: RADIUS.md, flexShrink: 0,
            background: newRemark.trim() ? "rgba(255,255,255,0.08)" : "transparent",
            border: `1px solid ${newRemark.trim() ? SA.lightSilver : SA.borderGray}`,
            color: newRemark.trim() ? SA.white : SA.silver,
            fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.12em", textTransform: "uppercase",
            fontFamily: "'Helvetica Neue',Arial,sans-serif", transition: "all 0.15s" }}>
          Add
        </Button>
      </div>

      {/* Reopen button — edit mode only */}
      {initialEntry && onReopen && initialEntry.status && initialEntry.status !== "PROTECTED" && (
        <div style={{ marginBottom: 8 }}>
          <Button fullWidth onClick={() => onReopen(initialEntry)}
            style={{ background: "transparent",
              border: `1px solid ${tokens.warningBorder}`,
              borderRadius: 2, color: tokens.warning, padding: "11px", fontSize: TYPOGRAPHY.tiny,
              letterSpacing: "0.18em", textTransform: "uppercase",
              fontFamily: "'Helvetica Neue',Arial,sans-serif", gap: 8 }}>
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 7C2 4.24 4.24 2 7 2C8.66 2 10.13 2.81 11.06 4.06" stroke={tokens.warning} strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M12 7C12 9.76 9.76 12 7 12C5.34 12 3.87 11.19 2.94 9.94" stroke={tokens.warning} strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M11.5 1.5L11.5 4.5L8.5 4.5" stroke={tokens.warning} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.5 12.5L2.5 9.5L5.5 9.5" stroke={tokens.warning} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Reopen — Return to Monitoring
          </Button>
        </div>
      )}

      {/* Delete button — edit mode only */}
      {initialEntry && onDelete && (
        <div style={{ marginBottom: 8 }}>
          {!confirmDel ? (
            <Button variant="danger" size="lg" fullWidth onClick={() => setConfirmDel(true)}
              style={{ borderRadius: 2, letterSpacing: "0.18em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
              Delete Connection
            </Button>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="secondary" size="lg" onClick={() => setConfirmDel(false)}
                style={{ flex: 1, borderRadius: 2, letterSpacing: "0.18em", textTransform: "uppercase",
                  fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                Cancel
              </Button>
              <Button variant="danger" size="lg" onClick={() => { onDelete(initialEntry.id); onClose(); }}
                style={{ flex: 1, borderRadius: 2, letterSpacing: "0.18em", textTransform: "uppercase",
                  fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                Confirm Delete
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  const validOutCount = outbounds.filter(o => o.outFlt.trim()).length;
  const saveLabel = initialEntry ? "Save Changes"
    : validOutCount > 1 ? `Log ${validOutCount} Connections`
    : "Log Connection";

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 100,
      display: "flex",
      alignItems: isWide ? "center" : "flex-end",
      justifyContent: isWide ? "center" : "stretch",
      padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...GLASS.modal,
        borderRadius: isWide ? 20 : "20px 20px 0 0",
        width: "100%",
        maxWidth: isWide ? 560 : "100%",
        maxHeight: isWide ? "92vh" : "78vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        animation: isWide ? "modalSlideUp 0.25s cubic-bezier(0.16,1,0.3,1)" : "sheetSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)",
        ...dragStyle
      }}>

        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "22px 20px 16px", borderBottom: `1px solid ${SA.borderGray}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.2em", textTransform: "uppercase" }}>
              {initialEntry ? "Edit Connection" : "New Connection Entry"}
            </span>
            <Button variant="icon" onClick={onClose}>×</Button>
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ padding: "16px 20px 0", flexShrink: 0 }}>
          {renderStepIndicator()}
        </div>

        {/* Step content — scrollable */}
        <div key={step} style={{ flex: 1, overflowY: "auto", padding: "0 20px" }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Navigation footer */}
        <div style={{ padding: "12px 20px 24px", borderTop: `1px solid ${SA.borderGray}`, flexShrink: 0,
          display: "flex", gap: 8, alignItems: "center" }}>
          {step > 1
            ? <Button variant="secondary" size="lg" onClick={() => setStep(s => s - 1)}
                style={{ letterSpacing: "0.1em", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                ← Back
              </Button>
            : <div />
          }
          <div style={{ flex: 1 }} />
          {step < 3
            ? <Button variant="primary" size="lg"
                disabled={step === 1 ? !step2Unlocked : !step3Unlocked}
                onClick={() => setStep(s => s + 1)}
                style={{ minWidth: isWide ? 180 : undefined,
                  letterSpacing: "0.1em", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                Next: {step === 1 ? "Outbound" : "Details"} →
              </Button>
            : <Button variant="primary" size="lg" onClick={handleSubmit}
                style={{ minWidth: isWide ? 180 : undefined,
                  letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                {saveLabel}
              </Button>
          }
        </div>

      </div>
    </div>
  );
}

export { AddEntryModal };
